import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { 
  MessageCircle, 
  CheckSquare, 
  Activity, 
  Moon, 
  Calendar,
  Sparkles,
  Heart,
  Film,
  ArrowRight,
  Grid3X3
} from 'lucide-react';

interface QuickLink {
  id: string;
  label: string;
  description: string;
  route: string;
}

interface QuickAccessGridProps {
  links: QuickLink[];
}

export const QuickAccessGrid: React.FC<QuickAccessGridProps> = ({ links }) => {
  const navigate = useNavigate();

  const getIcon = (id: string) => {
    const iconMap: Record<string, any> = {
      therapy: MessageCircle,
      checkin: CheckSquare,
      balance: Activity,
      inflammation: Activity,
      sleep: Moon,
      cycle: Calendar,
      telempathy: Sparkles,
      sanctuary: Heart,
      manifestation: Film
    };
    return iconMap[id] || Activity;
  };

  const getIconColor = (id: string) => {
    const colorMap: Record<string, string> = {
      therapy: 'text-primary',
      checkin: 'text-accent',
      balance: 'text-primary',
      inflammation: 'text-secondary',
      sleep: 'text-muted-foreground',
      cycle: 'text-primary',
      telempathy: 'text-accent',
      sanctuary: 'text-secondary',
      manifestation: 'text-primary'
    };
    return colorMap[id] || 'text-primary';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-muted/20 flex items-center justify-center">
            <Grid3X3 className="w-4 h-4 text-foreground/80" />
          </div>
          <h2 className="text-xl font-medium text-foreground tracking-wide">Quick Access</h2>
        </div>
        <button 
          onClick={() => navigate('/all-tools')}
          className="group flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors font-medium"
        >
          <span>All tools</span>
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        {links.map((link) => {
          const Icon = getIcon(link.id);
          const iconColor = getIconColor(link.id);
          
          return (
            <Card 
              key={link.id}
              className="group border-0 bg-card/40 backdrop-blur-sm hover:bg-card/70 hover:shadow-md transition-all duration-300 cursor-pointer"
              onClick={() => navigate(link.route)}
            >
              <CardContent className="p-4 sm:p-5">
                <div className="flex flex-col items-center text-center space-y-3">
                  <div className="w-12 h-12 rounded-xl bg-muted/15 flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                    <Icon className={`w-5 h-5 ${iconColor} group-hover:text-primary transition-colors`} />
                  </div>
                  <div className="space-y-1">
                    <div className="text-sm font-medium text-foreground tracking-wide">
                      {link.label}
                    </div>
                    <div className="text-xs text-foreground/70 font-normal line-clamp-2 hidden sm:block">
                      {link.description}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};